import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ['pending', 'awaiting_transfer', 'paid', 'processing', 'ready', 'picked_up', 'cancelled'];

const STATUS_LABELS = {
  pending: 'En attente',
  awaiting_transfer: 'Virement en attente',
  paid: 'Payée',
  processing: 'En préparation',
  ready: 'Prête',
  picked_up: 'Récupérée',
  cancelled: 'Annulée',
  shipped: 'Expédiée',
  delivered: 'Livrée',
};

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800',
  awaiting_transfer: 'bg-orange-100 text-orange-800',
  paid: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  ready: 'bg-teal-100 text-teal-800',
  picked_up: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`);
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/orders/${id}/status?status=${status}`);
      toast.success('Statut mis à jour');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteOrder = async (e, o) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement la commande ${o.orderNumber} ?\nCette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${o._id}`);
      toast.success('Commande supprimée');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Commandes</h1>
        <p className="text-gray-500 mt-1">{orders.length} commande(s)</p>
      </div>

      {loading ? (
        <div className="text-gray-500">Chargement...</div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
          Aucune commande pour le moment.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">N° Commande</th>
                <th className="text-left p-4 font-semibold text-gray-700">Client</th>
                <th className="text-right p-4 font-semibold text-gray-700">Montant</th>
                <th className="text-center p-4 font-semibold text-gray-700">Statut</th>
                <th className="text-center p-4 font-semibold text-gray-700">Paiement</th>
                <th className="text-left p-4 font-semibold text-gray-700">Date</th>
                <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admin-orders-table">
              {orders.map((o) => (
                <React.Fragment key={o._id}>
                  <tr
                    onClick={() => setExpanded(expanded === o._id ? null : o._id)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="p-4 font-mono font-semibold text-[#E42313]">{o.orderNumber}</td>
                    <td className="p-4">
                      <div className="font-medium">{o.customerName}</div>
                      <div className="text-xs text-gray-500">{o.customerEmail}</div>
                    </td>
                    <td className="p-4 text-right font-semibold">{o.totalAmount.toFixed(2)} €</td>
                    <td className="p-4 text-center">
                      <select
                        value={o.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateStatus(o._id, e.target.value)}
                        className={`text-xs px-3 py-1 rounded-full border-0 font-semibold cursor-pointer ${STATUS_STYLES[o.status] || 'bg-gray-100'}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABELS[s] || s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4 text-center">
                      <span
                        className={`text-xs px-2 py-1 rounded font-medium ${
                          o.paymentStatus === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {o.paymentStatus || 'pending'}
                      </span>
                    </td>
                    <td className="p-4 text-gray-600 text-xs">
                      {new Date(o.createdAt).toLocaleString('fr-FR')}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={(e) => deleteOrder(e, o)}
                        className="p-2 text-gray-500 hover:text-red-600 inline-flex"
                        title="Supprimer la commande"
                        data-testid={`delete-order-${o._id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  {expanded === o._id && (
                    <tr className="bg-gray-50">
                      <td colSpan={7} className="p-6">
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-semibold text-gray-800 mb-2">Contact</h4>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div>{o.customerName}</div>
                              <div>{o.customerEmail}</div>
                              <div>{o.customerPhone}</div>
                            </div>
                            <h4 className="font-semibold text-gray-800 mt-4 mb-2">
                              {o.fulfillment === 'shipping' ? 'Livraison' : 'Retrait (Click & Collect)'}
                            </h4>
                            <div className="text-sm text-gray-600 space-y-1">
                              {o.fulfillment === 'shipping' && o.shippingAddress ? (
                                <>
                                  <div>{o.shippingAddress.street}</div>
                                  <div>
                                    {o.shippingAddress.postalCode} {o.shippingAddress.city}
                                  </div>
                                  <div>{o.shippingAddress.country}</div>
                                </>
                              ) : (
                                <div className="whitespace-pre-line">
                                  {o.pickupAddress || '17 rue du Dépôt, 01500 Ambérieu-en-Bugey'}
                                </div>
                              )}
                              {o.paymentMethod && (
                                <div className="pt-2 text-xs text-gray-500">
                                  Paiement : {o.paymentMethod === 'transfer' ? 'Virement bancaire' : 'Carte / Stripe'}
                                </div>
                              )}
                              {o.notes && (
                                <div className="pt-2">
                                  <span className="text-xs font-semibold text-gray-700">Notes :</span>
                                  <div className="text-xs whitespace-pre-line">{o.notes}</div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 mb-2">Articles</h4>
                            <div className="space-y-2">
                              {o.items.map((it, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span>
                                    {it.name} <span className="text-gray-500">×{it.quantity}</span>
                                  </span>
                                  <span className="font-semibold">
                                    {(it.price * it.quantity).toFixed(2)} €
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
